import { Dispatch, SetStateAction, useCallback } from 'react';
import * as Types from '../types/types';
import { ApiClient, CreateEmployerRequest } from 'services/api-client';
import {
  AuthState,
  EmailVerificationRequest,
  EmailVerificationResponse,
  PasswordResetResult,
} from './authTypes';
import { getStoredAuthData, storeAuthData, TOKEN_STORAGE } from './authStorage';
import { parseApiErrorMessage } from './authUtils';

interface SecondaryAuthActionDeps {
  setAuthState: Dispatch<SetStateAction<AuthState>>;
  apiClient: ApiClient;
  refreshAccessToken: (refreshToken: string) => Promise<Types.AuthResponse | null>;
  logout: () => Promise<void>;
}

export function useAuthSecondaryActions({
  setAuthState,
  apiClient,
  refreshAccessToken,
  logout,
}: SecondaryAuthActionDeps): {
  verifyEmail: (
    verificationData: EmailVerificationRequest
  ) => Promise<EmailVerificationResponse | null>;
  resendEmailVerification: (email: string) => Promise<boolean>;
  setPendingVerificationEmail: (email: string) => void;
  getPendingVerificationEmail: () => string | null;
  createEmployerAccount: (employerData: CreateEmployerRequest) => Promise<boolean>;
  requestPasswordReset: (email: string) => Promise<boolean>;
  confirmPasswordReset: (token: string, newPassword: string) => Promise<PasswordResetResult>;
  refreshAuth: () => Promise<boolean>;
  resendMFACode: (email: string, deviceId: string, deviceName: string) => Promise<boolean>;
  clearMFA: () => void;
} {
  const verifyEmail = useCallback(
    async (
      verificationData: EmailVerificationRequest
    ): Promise<EmailVerificationResponse | null> => {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

      try {
        const result = await apiClient.verifyEmail(verificationData);
        setAuthState(prev => ({ ...prev, isLoading: false }));
        return {
          message: result.message || 'Email verified successfully',
          userType: result.userType || 'user',
        };
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Email verification failed';
        setAuthState(prev => ({ ...prev, isLoading: false, error: errorMessage }));
        return null;
      }
    },
    [apiClient, setAuthState]
  );

  const resendEmailVerification = useCallback(
    async (email: string): Promise<boolean> => {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
      try {
        await apiClient.resendVerificationEmail({ email });
        setAuthState(prev => ({ ...prev, isLoading: false }));
        return true;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to resend verification email';
        setAuthState(prev => ({ ...prev, isLoading: false, error: errorMessage }));
        return false;
      }
    },
    [apiClient, setAuthState]
  );

  const setPendingVerificationEmail = useCallback((email: string) => {
    localStorage.setItem(TOKEN_STORAGE.PENDING_VERIFICATION_EMAIL, email);
  }, []);

  const getPendingVerificationEmail = useCallback((): string | null => {
    return localStorage.getItem(TOKEN_STORAGE.PENDING_VERIFICATION_EMAIL);
  }, []);

  const createEmployerAccount = useCallback(
    async (employerData: CreateEmployerRequest): Promise<boolean> => {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
      try {
        await apiClient.createEmployer(employerData);
        setPendingVerificationEmail(employerData.email);
        setAuthState(prev => ({ ...prev, isLoading: false }));
        return true;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Account creation failed';
        setAuthState(prev => ({ ...prev, isLoading: false, error: errorMessage }));
        return false;
      }
    },
    [apiClient, setAuthState, setPendingVerificationEmail]
  );

  const confirmPasswordReset = useCallback(
    async (token: string, newPassword: string): Promise<PasswordResetResult> => {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
      try {
        await apiClient.confirmPasswordReset({ token, newPassword });
        setAuthState(prev => ({ ...prev, isLoading: false }));
        return { ok: true };
      } catch (error) {
        const rawMessage = error instanceof Error ? error.message : 'Password reset failed';
        const { status, message } = parseApiErrorMessage(rawMessage);
        const tokenInvalid = status === 400;
        setAuthState(prev => ({ ...prev, isLoading: false, error: message }));
        return { ok: false, error: message, tokenInvalid };
      }
    },
    [apiClient, setAuthState]
  );

  const requestPasswordReset = useCallback(
    async (email: string): Promise<boolean> => {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
      try {
        await apiClient.requestPasswordReset({ email });
        setAuthState(prev => ({ ...prev, isLoading: false }));
        return true;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Password reset request failed';
        setAuthState(prev => ({ ...prev, isLoading: false, error: errorMessage }));
        return false;
      }
    },
    [apiClient, setAuthState]
  );

  const refreshAuth = useCallback(async (): Promise<boolean> => {
    const stored = getStoredAuthData();
    if (!stored.refreshToken) {
      return false;
    }

    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    const refreshResult = await refreshAccessToken(stored.refreshToken);
    if (!refreshResult) {
      await logout();
      return false;
    }

    const isGuest = stored.userType === 'guest';
    storeAuthData(refreshResult, isGuest);
    apiClient.setAuthToken(refreshResult.accessToken);
    setAuthState(prev => ({
      ...prev,
      user: isGuest ? null : refreshResult.user,
      guest: isGuest ? (refreshResult.user as Types.Guest) : null,
      isAuthenticated: true,
      isGuest,
      isLoading: false,
      error: null,
    }));
    return true;
  }, [apiClient, logout, refreshAccessToken, setAuthState]);

  const resendMFACode = useCallback(
    async (email: string, deviceId: string, deviceName: string): Promise<boolean> => {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
      try {
        await apiClient.requestMFA({ email, password: '', deviceId, deviceName });
        setAuthState(prev => ({ ...prev, isLoading: false }));
        return true;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to resend MFA code';
        setAuthState(prev => ({ ...prev, isLoading: false, error: errorMessage }));
        return false;
      }
    },
    [apiClient, setAuthState]
  );

  const clearMFA = useCallback(() => {
    setAuthState(prev => ({ ...prev, mfaResponse: null, error: null }));
  }, [setAuthState]);

  return {
    verifyEmail,
    resendEmailVerification,
    setPendingVerificationEmail,
    getPendingVerificationEmail,
    createEmployerAccount,
    requestPasswordReset,
    confirmPasswordReset,
    refreshAuth,
    resendMFACode,
    clearMFA,
  };
}
